import type { FC } from "react";
import { useState } from "react";
import "../../common/table/data-table.css";
import "../../index.css";
import { publishManager } from "./publish-manager";
import { useSnapshot } from "valtio";
import { Button } from "@renewmap/ui/button";
import { Undo2 } from "lucide-react";
import { GeomInfoEditPanel, RowWithGeom, selectedRowState } from "./geom-info";
import { ChangedEntries } from "./changed-entries-table";
import { UpdateEntriesTable, setChangelogPoint } from "./updates-table";
import { PublishButton } from "./publish-button";
import NoChangesInfo from "./no-changes-info";
import { ToolsAppWrapper, ToolData } from "../../common/tools-app-wrapper";
import { TableControls } from "../../common/table/table-controls";
import { InvalidEntityRecordsCard } from "./invalid-entity-records-table";
import { Point } from "geojson";
import { PublishStateResponse } from "@renewmap/model";

const loadPublishState = async (): Promise<PublishStateResponse> => {
  const res = await fetch(`/api/publish/diff`);

  if (!res.ok) {
    const errorData = (await res.json().catch(() => null)) as {
      message?: string;
    } | null;
    throw new Error(`[${res.status}] ${errorData?.message || res.statusText}`);
  }

  const data = (await res.json()) as PublishStateResponse;
  publishManager.init(data);
  return data;
};

const MainContent: FC = () => {
  const {
    types,
    score,
    markAllDiffsState: { marking, markedIds },
  } = useSnapshot(publishManager.publishState);
  const { selectedRow, type, changelogPoint } = useSnapshot(selectedRowState);
  const [quickSearchValue, setQuickSearch] = useState<string>("");
  const [showAllColumns, setShowAllColumns] = useState(false);

  const isChangelogSelected = type === "changelog";

  const hasEntries = Object.values(types).flatMap((t) => t.entries).length > 0;

  return (
    <>
      <div className="mb-4 flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={marking}
          onClick={() => publishManager.markAllDiffs()}
        >
          Find all diffs
        </Button>
        {Object.keys(markedIds).length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            disabled={marking}
            onClick={() => publishManager.unmarkAllDiffs()}
          >
            <Undo2 className="h-4 w-4" />
          </Button>
        )}
      </div>
      {hasEntries ? (
        <div className="flex gap-3">
          <div className="flex flex-grow flex-col gap-3 rounded-xl bg-white p-4">
            <InvalidEntityRecordsCard />
            <div>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-lg font-semibold">Draft → Public</h2>
                <TableControls
                  loading={false}
                  setQuickSearch={setQuickSearch}
                  showAllColumns={showAllColumns}
                  setShowAllColumns={setShowAllColumns}
                />
              </div>
              <ChangedEntries
                quickSearchValue={quickSearchValue}
                showAllColumns={showAllColumns}
              />
            </div>
            <UpdateEntriesTable />
            <div className="flex w-full gap-3">
              <div className="ml-auto flex gap-2">
                <PublishButton />
              </div>
            </div>
          </div>
          <GeomInfoEditPanel
            selectedRow={selectedRow as RowWithGeom | null}
            changelogPoint={
              isChangelogSelected ? (changelogPoint as Point | null) : undefined
            }
            setChangelogPoint={
              isChangelogSelected ? setChangelogPoint : undefined
            }
          />
        </div>
      ) : (
        <div className="rounded-xl bg-white/50 backdrop-blur-sm">
          <NoChangesInfo number={score} />
        </div>
      )}
    </>
  );
};

export const Main: FC = () => {
  return (
    <ToolsAppWrapper>
      <ToolData load={loadPublishState} cacheKey="publish">
        <MainContent />
      </ToolData>
    </ToolsAppWrapper>
  );
};
